import * as THREE from 'three/webgpu';
import {isSketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import {
    createSketchCloneContext,
    type ISketchObject,
    SketchGroup,
} from '../../mesh/sketch';
import {isSketchPointObject} from '../../mesh/guards';
import {isSketchContour} from '../../mesh/meshProxy/SketchContour';
import {HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON} from '../../sketch/SketchDefinitionCommitReasons';
import {SketchCircularPatternObject} from '../../sketch/circular-pattern/SketchCircularPatternObject';
import {scheduleDisposableResourceDisposal} from '../../utils/scheduleDisposableResourceDisposal';
import {ownsObject3DGeometry} from '../../utils/scene-object/ownsObject3DGeometry';
import type {
    SketchGroupRuntimeDisplayState,
    SketchGroupRuntimeHistorySnapshot,
} from './SketchGroupRuntimeHistoryTypes';

/**
 * 草图组运行时快照注册表配置。
 */
export interface SketchGroupRuntimeSnapshotRegistryOptions {
    /**
     * 根据草图 ID 解析当前场景里的草图组。
     *
     * @param sketchId 草图组 ID。
     * @returns 草图组；不存在时返回 null。
     */
    readonly resolveSketchGroup: (sketchId: string) => SketchGroup | null;
    /**
     * 读取草图组显示态。
     *
     * @param sketchId 草图组 ID。
     * @returns 草图显示态；未命中时返回 null。
     */
    readonly resolveSketchGroupDisplayState?: (sketchId: string) => SketchGroupRuntimeDisplayState | null;
    /**
     * 解析草图在设计树中的稳定顺序。
     *
     * @param sketchId 草图组 ID。
     * @returns 设计树顺序；无法解析时返回 null。
     */
    readonly resolveSketchGroupOrder?: (sketchId: string) => number | null;
    /**
     * 解析草图在设计树中的创建时间戳。
     *
     * @param sketchId 草图组 ID。
     * @returns 创建时间戳（毫秒）；无法解析时返回 null。
     */
    readonly resolveSketchGroupCreatedAt?: (sketchId: string) => number | null;
    /**
     * 当快照目标草图组已经被删除时，重新把快照克隆体注册回运行时。
     *
     * @param input 待恢复的草图组与显示态。
     * @returns 实际进入运行时的草图组；失败时返回 null。
     */
    readonly restoreMissingSketchGroup?: (input: RestoreMissingSketchGroupInput) => SketchGroup | null;
    /**
     * 请求视口重绘。
     */
    readonly requestRender: () => void;
}

/**
 * 恢复已删除草图组的输入。
 */
export interface RestoreMissingSketchGroupInput {
    /** 从快照克隆出的草图组。 */
    readonly sketchGroup: SketchGroup;
    /** 快照捕获时的显示态。 */
    readonly displayState: SketchGroupRuntimeDisplayState | null;
    /** 快照捕获时的设计树顺序。 */
    readonly order: number | null;
    /** 快照捕获时的设计树创建时间戳。 */
    readonly createdAt: number | null;
}

/**
 * 捕获草图组运行时快照的输入。
 */
export interface CaptureSketchGroupRuntimeSnapshotInput {
    /** 草图组。 */
    readonly sketchGroup: SketchGroup;
    /** 快照标签。 */
    readonly label: string;
}

/**
 * 草图组运行时快照注册表。
 */
export interface SketchGroupRuntimeSnapshotRegistry {
    /**
     * 捕获草图组运行时快照。
     *
     * @param input 捕获输入。
     * @returns 可进入 history patch 的轻量快照。
     */
    readonly capture: (input: CaptureSketchGroupRuntimeSnapshotInput) => SketchGroupRuntimeHistorySnapshot;
    /**
     * 恢复草图组运行时快照。
     *
     * @param snapshot 轻量快照。
     */
    readonly restore: (snapshot: SketchGroupRuntimeHistorySnapshot) => void;
    /**
     * 判断快照是否仍然等于当前草图组状态。
     *
     * @param snapshot 轻量快照。
     * @returns 当前草图组是否仍处于该快照状态。
     */
    readonly matchesCurrent: (snapshot: SketchGroupRuntimeHistorySnapshot) => boolean;
    /**
     * 释放指定运行时快照。
     *
     * @param snapshot 轻量快照。
     */
    readonly release: (snapshot: SketchGroupRuntimeHistorySnapshot) => void;
    /**
     * 清理所有运行时快照。
     */
    readonly clear: () => void;
}

/**
 * 可写 UUID 的 Object3D 视图。
 */
type WritableObjectUuid = THREE.Object3D & {
    /** Three.js 运行时 UUID。 */
    uuid: string;
};

/**
 * 运行时快照条目。
 */
interface RuntimeSnapshotEntry {
    /** 草图组 ID。 */
    readonly sketchId: string;
    /** 克隆得到的草图组副本。 */
    readonly group: SketchGroup;
}

/**
 * 可释放的 Three.js 资源。
 */
interface DisposableResource {
    /** 释放资源。 */
    dispose: () => void;
}

/**
 * 生成草图组运行时快照 ID。
 *
 * @param sketchId 草图组 ID。
 * @param index 快照递增序号。
 * @returns 快照 ID。
 */
function createRuntimeSnapshotId(sketchId: string, index: number): string {
    return `sketch-runtime:${sketchId}:${index}`;
}

/**
 * 写入 Object3D UUID。
 *
 * 设计说明：
 * Three.js 克隆会生成新 uuid，但 CAD 历史必须把用户感知实体恢复到原 ID；
 * 这里仅在快照克隆与恢复克隆阶段写入，不影响运行中普通对象创建。
 *
 * @param object 目标对象。
 * @param uuid 需要恢复的 UUID。
 */
function setObjectUuid(object: THREE.Object3D, uuid: string): void {
    (object as WritableObjectUuid).uuid = uuid;
}

/**
 * 遍历对象树。
 *
 * @param root 根对象。
 * @returns 深度优先对象列表。
 */
function collectObjectTree(root: THREE.Object3D): THREE.Object3D[] {
    const objects: THREE.Object3D[] = [];

    root.traverse((object) => {
        objects.push(object);
    });

    return objects;
}

//------ 椭圆轴控制点历史可见性 开始------
/**
 * 判断对象可见性是否只属于草图交互临时反馈。
 *
 * 设计说明：
 * 1. 圆和圆弧的象限点会在吸附和绘制过程中短暂显示；
 * 2. 这些点不是 CAD 主状态，不能进入 undo/redo 快照，否则恢复后会把临时吸附点永久留在场景里；
 * 3. 椭圆长短轴点虽然也标记为象限点，但它们是可编辑控制点，必须保留为模型显示状态；
 * 4. 当前只收口纯临时象限点，避免误伤端点、中心点、椭圆轴点和用户创建的真实草图点。
 *
 * @param object 需要判断的场景对象。
 * @returns 仅代表临时交互可见性时返回 true。
 */
function isTransientSketchRuntimeVisiblePoint(object: THREE.Object3D): boolean {
    return isSketchPointObject(object)
        && object.isQuadrantPoint
        && !object.isEllipseAxisControlPoint();
}

/**
 * 解析草图拓扑点的历史规范化可见性。
 *
 * @param object 需要解析的场景对象。
 * @returns 规范化后的可见性；返回 null 表示沿用对象当前可见性。
 */
function resolveSketchRuntimeHistoryNormalizedVisibility(object: THREE.Object3D): boolean | null {
    if (!isSketchPointObject(object)) {
        return null;
    }
    if (object.isEndPoint) {
        return true;
    }
    if (object.isMidPoint || isTransientSketchRuntimeVisiblePoint(object)) {
        return false;
    }
    return null;
}
//------ 椭圆轴控制点历史可见性 结束------

/**
 * 规范化草图运行时历史快照中的临时显示状态。
 *
 * 设计说明：
 * 1. 历史快照只记录模型定义，不记录吸附策略、悬停状态、绘制预览造成的临时可见性；
 * 2. 这里在快照副本层处理，避免修改当前用户正在交互的真实场景对象；
 * 3. 恢复前再次规范化，防止旧内存快照或后续扩展入口绕过采集阶段。
 *
 * @param root 需要规范化的对象树根节点。
 */
function normalizeSketchRuntimeHistoryVisualState(root: THREE.Object3D): void {
    root.traverse((object) => {
        if (isSketchPointObject(object)) {
            object.refreshDefaultVisibilityByTopologyRole();
        }
    });
}

/**
 * 获取用于历史签名的可见性值。
 *
 * @param object 草图对象。
 * @returns 稳定签名中的可见性片段。
 */
function resolveVisibleSignatureValue(object: THREE.Object3D): string {
    const normalizedVisibility = resolveSketchRuntimeHistoryNormalizedVisibility(object);
    if (normalizedVisibility !== null) {
        return String(normalizedVisibility);
    }
    return String(object.visible);
}

/**
 * 判断输入是否为可释放资源。
 *
 * @param value 待判断资源。
 * @returns 是否存在 dispose 方法。
 */
function isDisposableResource(value: unknown): value is DisposableResource {
    return typeof (value as DisposableResource | null)?.dispose === 'function';
}

/**
 * 判断材质是否由当前对象独占。
 *
 * @param object 目标场景对象。
 * @returns 允许由当前对象释放 material 时返回 true。
 */
function ownsRenderableMaterial(object: THREE.Object3D): boolean {
    /**
     * 草图线、草图点的材质来自工厂复用。历史快照真正独占的材质目前只有上色草图轮廓，
     * 因此快照释放时只回收轮廓材质，避免误伤实时场景仍在使用的共享材质。
     */
    return isSketchContour(object);
}

/**
 * 释放 Object3D 持有的几何体和材质。
 *
 * @param object 目标对象。
 * @param disposeMaterial 是否释放材质。
 */
function disposeObjectRenderResources(object: THREE.Object3D, disposeMaterial: boolean): void {
    const renderableObject = object as THREE.Object3D & {
        /** 可选几何体。 */
        geometry?: unknown;
        /** 可选材质。 */
        material?: unknown;
    };

    if (ownsObject3DGeometry(object) && isDisposableResource(renderableObject.geometry)) {
        scheduleDisposableResourceDisposal(renderableObject.geometry);
    }

    if (!disposeMaterial || !ownsRenderableMaterial(object)) {
        return;
    }

    const material = renderableObject.material;

    if (Array.isArray(material)) {
        material.forEach((item) => {
            if (isDisposableResource(item)) {
                scheduleDisposableResourceDisposal(item);
            }
        });
        return;
    }

    if (isDisposableResource(material)) {
        scheduleDisposableResourceDisposal(material);
    }
}

/**
 * 释放运行时快照草图组。
 *
 * @param sketchGroup 快照草图组。
 */
function disposeSnapshotGroup(sketchGroup: SketchGroup): void {
    collectObjectTree(sketchGroup).forEach((object) => {
        disposeObjectRenderResources(object, true);
    });
    sketchGroup.dispose();
    sketchGroup.clear();
}

/**
 * 复制 Object3D 树的 UUID。
 *
 * @param sourceRoot 源对象树。
 * @param targetRoot 目标对象树。
 */
function copyObjectTreeUuids(sourceRoot: THREE.Object3D, targetRoot: THREE.Object3D): void {
    const sourceObjects = collectObjectTree(sourceRoot);
    const targetObjects = collectObjectTree(targetRoot);

    /**
     * 部分复合草图对象会在 cloneSelf 中重建辅助子树。这里按共同前缀恢复 UUID，
     * 对无法一一对应的辅助对象保留 Three.js 新 UUID，避免快照捕获因非主实体结构差异失败。
     */
    const copyLength = Math.min(sourceObjects.length, targetObjects.length);
    sourceObjects.slice(0, copyLength).forEach((sourceObject, index) => {
        setObjectUuid(targetObjects[index], sourceObject.uuid);
    });
}

/**
 * 克隆草图组并保留 CAD 运行时 UUID。
 *
 * @param sketchGroup 源草图组。
 * @returns 保留原 UUID 的草图组副本。
 */
function cloneSketchGroupWithStableIds(sketchGroup: SketchGroup): SketchGroup {
    const cloneContext = createSketchCloneContext();
    const clonedGroup = sketchGroup.cloneSelf(cloneContext);

    /**
     * cloneSelf 负责复制共享点和多目标约束；这里额外恢复 uuid，保证 undo/redo 后
     * 选择、约束容器、下游引用仍能继续用同一批稳定对象 ID 定位。
     */
    copyObjectTreeUuids(sketchGroup, clonedGroup);
    /**
     * 运行时历史快照代表同一个 CAD 草图实体，克隆体必须保留用户可见名称；
     * 通用 cloneSelf 仍保持“新对象新名称”，避免影响复制草图命令。
     */
    clonedGroup.name = sketchGroup.name;
    cloneContext.objectMap.forEach((clonedObject, sourceUuid) => {
        setObjectUuid(clonedObject as unknown as THREE.Object3D, sourceUuid);
    });
    cloneContext.pointMap.forEach((clonedPoint, sourceUuid) => {
        setObjectUuid(clonedPoint, sourceUuid);
    });

    normalizeSketchRuntimeHistoryVisualState(clonedGroup);

    return clonedGroup;
}

/**
 * 将数字格式化为稳定签名片段。
 *
 * @param value 数值。
 * @returns 稳定字符串。
 */
function formatNumberForSignature(value: number): string {
    return Number.isFinite(value) ? value.toFixed(9) : 'NaN';
}

/**
 * 收集对象约束签名。
 *
 * @param object 草图对象。
 * @returns 约束签名片段。
 */
function collectConstraintSignature(object: THREE.Object3D): string {
    const constraints = (object as Partial<ISketchObject>).constraints;

    if (!constraints || !isSketchRelationContainer(constraints)) {
        return '';
    }

    return constraints.getRelationSnapshots()
        .map((snapshot) => `${snapshot.id}:${snapshot.name}:${String(snapshot.relationType)}`)
        .sort()
        .join(',');
}

/**
 * 读取稳定向量签名。
 *
 * @param value 候选向量。
 * @returns 向量签名；不是向量时返回空字符串。
 */
function collectVectorSignature(value: unknown): string {
    if (!value || typeof value !== 'object') {
        return '';
    }

    const x = Reflect.get(value, 'x');
    const y = Reflect.get(value, 'y');
    const z = Reflect.get(value, 'z');
    if (typeof x !== 'number' || typeof y !== 'number') {
        return '';
    }

    return [
        formatNumberForSignature(x),
        formatNumberForSignature(y),
        typeof z === 'number' ? formatNumberForSignature(z) : '0.000000000',
    ].join(',');
}

/**
 * 收集草图实体语义签名。
 *
 * 设计说明：
 * 运行时草图对象的 Object3D transform 不一定覆盖圆半径、圆弧角度、构造线状态、
 * 多边形边数等 CAD 语义字段。签名必须覆盖这些字段，否则属性编辑和约束目标变化可能被误判为空事务。
 *
 * @param object 草图对象。
 * @returns 草图实体语义签名。
 */
function collectSketchGeometrySignature(object: THREE.Object3D): string {
    if (object instanceof SketchCircularPatternObject) {
        return JSON.stringify(object.getDefinitionSnapshot());
    }
    const scalarKeys = [
        'radius',
        'majorRadius',
        'minorRadius',
        'startAngle',
        'endAngle',
        'sweepAngle',
        'segmentCount',
        'sideCount',
        'isConstruction',
        'isConstructionLine',
        'construction',
    ];
    const methodKeys = [
        'getPosition',
        'getCenter',
        'getCenterPoint',
        'getStartPoint',
        'getEndPoint',
        'getStartPosition',
        'getEndPosition',
    ];
    const scalarSignature = scalarKeys
        .map((key) => {
            const value = Reflect.get(object, key);
            if (typeof value === 'number') {
                return `${key}:${formatNumberForSignature(value)}`;
            }
            if (typeof value === 'string' || typeof value === 'boolean') {
                return `${key}:${String(value)}`;
            }
            return '';
        })
        .filter(Boolean)
        .join(',');
    const methodSignature = methodKeys
        .map((key) => {
            const method = Reflect.get(object, key);
            if (typeof method !== 'function') {
                return '';
            }
            try {
                const signature = collectVectorSignature(method.call(object));
                return signature ? `${key}:${signature}` : '';
            } catch {
                return '';
            }
        })
        .filter(Boolean)
        .join(',');

    return [scalarSignature, methodSignature].filter(Boolean).join(';');
}

/**
 * 创建草图组签名。
 *
 * @param sketchGroup 草图组。
 * @returns 签名字符串。
 */
function createSketchGroupSignature(sketchGroup: SketchGroup): string {
    const geometrySignature = collectObjectTree(sketchGroup)
        .map((object) => {
            const sketchObject = object as Partial<ISketchObject>;
            return [
                object.uuid,
                object.type,
                object.name,
                resolveVisibleSignatureValue(object),
                sketchObject.objectType ?? '',
                formatNumberForSignature(object.position.x),
                formatNumberForSignature(object.position.y),
                formatNumberForSignature(object.position.z),
                formatNumberForSignature(object.quaternion.x),
                formatNumberForSignature(object.quaternion.y),
                formatNumberForSignature(object.quaternion.z),
                formatNumberForSignature(object.quaternion.w),
                formatNumberForSignature(object.scale.x),
                formatNumberForSignature(object.scale.y),
                formatNumberForSignature(object.scale.z),
                collectSketchGeometrySignature(object),
                collectConstraintSignature(object),
            ].join('|');
        })
        .join(';');
    const referenceTargetSignature = Array.from(sketchGroup.getSketchReferenceTargets())
        .map((referenceTarget) => [
            referenceTarget.uuid,
            referenceTarget.persistentId,
            referenceTarget.name,
            JSON.stringify(referenceTarget.toPersistentRecord().reference),
            collectConstraintSignature(referenceTarget),
        ].join('|'))
        .sort()
        .join(';');
    return `${geometrySignature}||referenceTargets:${referenceTargetSignature}`;
}

/**
 * 替换草图组子树。
 *
 * @param targetGroup 当前场景中的草图组。
 * @param sourceGroup 快照草图组。
 */
function replaceSketchGroupChildren(targetGroup: SketchGroup, sourceGroup: SketchGroup): void {
    const nextGroup = cloneSketchGroupWithStableIds(sourceGroup);
    const removedChildren = [...targetGroup.children];
    const removedReferenceTargets = [...targetGroup.getSketchReferenceTargets()];
    const nextReferenceTargets = [...nextGroup.getSketchReferenceTargets()];

    /**
     * 先移除当前子对象，再把快照副本逐个挂回原草图组。保留原 SketchGroup 实例可以避免
     * SketchGroupManager、特征树和 UI 状态持有的草图组引用失效。
     */
    targetGroup.runRuntimeRestoreScope(() => {
        removedChildren.forEach((child) => {
            targetGroup.remove(child);
        });
        removedReferenceTargets.forEach((referenceTarget) => {
            targetGroup.removeSketchReferenceTarget(referenceTarget);
        });

        [...nextGroup.children].forEach((child) => {
            targetGroup.add(child);
        });
        nextReferenceTargets.forEach((referenceTarget) => {
            nextGroup.removeSketchReferenceTarget(referenceTarget);
            targetGroup.addSketchReferenceTarget(referenceTarget);
        });

        targetGroup.position.copy(nextGroup.position);
        targetGroup.quaternion.copy(nextGroup.quaternion);
        targetGroup.scale.copy(nextGroup.scale);
        targetGroup.visible = nextGroup.visible;
        targetGroup.name = nextGroup.name;
        targetGroup.renderOrder = nextGroup.renderOrder;
        targetGroup.frustumCulled = nextGroup.frustumCulled;
        targetGroup.layers.mask = nextGroup.layers.mask;
    });
    /**
     * nextGroup 只是为了生成“将要挂回真实场景”的中转壳。它的子对象已经移动到 targetGroup，
     * 不能再按快照对象生命周期处理；这里只断开空壳引用，避免未来 Three.js 父子转移顺序变化时
     * 误解绑或释放已经进入真实场景的对象。
     */
    nextGroup.clear();

    /**
     * 被替换下来的子树不会再被历史系统复用，必须释放其 GPU 资源。
     * 历史可恢复状态来自运行时快照副本，而不是这些离场对象本身。
     */
    removedChildren.forEach((child) => {
        collectObjectTree(child).forEach((object) => {
            /**
             * 当前场景中的草图基础材质可能由工厂复用，只释放几何体；
             * 历史快照克隆体由 disposeSnapshotGroup 负责完整释放材质。
             */
            disposeObjectRenderResources(object, false);
        });
        child.clear();
    });
    removedReferenceTargets.forEach((referenceTarget) => {
        referenceTarget.dispose();
    });
}

/**
 * 创建草图组运行时快照注册表。
 *
 * @param options 注册表配置。
 * @returns 草图组运行时快照注册表。
 */
export function createSketchGroupRuntimeSnapshotRegistry(
    options: SketchGroupRuntimeSnapshotRegistryOptions,
): SketchGroupRuntimeSnapshotRegistry {
    let snapshotIndex = 0;
    const runtimeSnapshotMap = new Map<string, RuntimeSnapshotEntry>();

    return {
        capture(input) {
            snapshotIndex += 1;
            const runtimeSnapshotId = createRuntimeSnapshotId(input.sketchGroup.uuid, snapshotIndex);
            const clonedGroup = cloneSketchGroupWithStableIds(input.sketchGroup);
            const displayState = options.resolveSketchGroupDisplayState?.(input.sketchGroup.uuid) ?? null;
            const order = options.resolveSketchGroupOrder?.(input.sketchGroup.uuid) ?? null;
            const createdAt = options.resolveSketchGroupCreatedAt?.(input.sketchGroup.uuid) ?? null;

            runtimeSnapshotMap.set(runtimeSnapshotId, {
                sketchId: input.sketchGroup.uuid,
                group: clonedGroup,
            });

            return {
                sketchId: input.sketchGroup.uuid,
                name: input.sketchGroup.name,
                order,
                createdAt,
                runtimeSnapshotId,
                revision: input.sketchGroup.definitionRevision,
                objectCount: input.sketchGroup.getSketchObjects().size,
                displayState,
                signature: createSketchGroupSignature(input.sketchGroup),
                persistent: false,
                capturedAt: Date.now(),
                label: input.label,
            };
        },
        restore(snapshot) {
            const entry = runtimeSnapshotMap.get(snapshot.runtimeSnapshotId);

            if (!entry) {
                throw new Error(`草图运行时快照不存在：${snapshot.runtimeSnapshotId}`);
            }

            if (entry.sketchId !== snapshot.sketchId) {
                throw new Error(`草图运行时快照归属不一致：snapshot=${snapshot.sketchId}, entry=${entry.sketchId}`);
            }

            let targetGroup = options.resolveSketchGroup(snapshot.sketchId);

            if (!targetGroup && options.restoreMissingSketchGroup) {
                const restoredGroup = cloneSketchGroupWithStableIds(entry.group);
                targetGroup = options.restoreMissingSketchGroup({
                    sketchGroup: restoredGroup,
                    displayState: snapshot.displayState,
                    order: snapshot.order,
                    createdAt: snapshot.createdAt,
                });

                if (!targetGroup) {
                    disposeSnapshotGroup(restoredGroup);
                    throw new Error(`无法从运行时快照重新注册草图组：sketchId=${snapshot.sketchId}`);
                }
                if (targetGroup !== restoredGroup) {
                    disposeSnapshotGroup(restoredGroup);
                }

                targetGroup.markDefinitionDirty({
                    reason: HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON,
                    sourceObjectId: snapshot.sketchId,
                });
                targetGroup.commitDefinitionChanges({
                    reason: HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON,
                    sourceObjectId: snapshot.sketchId,
                });
                options.requestRender();
                return;
            }

            if (!targetGroup) {
                throw new Error(`无法恢复草图运行时快照：草图组不存在，sketchId=${snapshot.sketchId}`);
            }

            replaceSketchGroupChildren(targetGroup, entry.group);
            targetGroup.markDefinitionDirty({
                reason: HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON,
                sourceObjectId: snapshot.sketchId,
            });
            targetGroup.commitDefinitionChanges({
                reason: HISTORY_SKETCH_RUNTIME_RESTORE_COMMIT_REASON,
                sourceObjectId: snapshot.sketchId,
            });
            options.requestRender();
        },
        matchesCurrent(snapshot) {
            const targetGroup = options.resolveSketchGroup(snapshot.sketchId);

            if (!targetGroup) {
                return false;
            }

            return createSketchGroupSignature(targetGroup) === snapshot.signature
                && targetGroup.getSketchObjects().size === snapshot.objectCount;
        },
        release(snapshot) {
            const entry = runtimeSnapshotMap.get(snapshot.runtimeSnapshotId);

            if (!entry) {
                return;
            }

            disposeSnapshotGroup(entry.group);
            runtimeSnapshotMap.delete(snapshot.runtimeSnapshotId);
        },
        clear() {
            runtimeSnapshotMap.forEach((entry) => {
                disposeSnapshotGroup(entry.group);
            });
            runtimeSnapshotMap.clear();
        },
    };
}
